import { useTranslate } from '@repo/shared/hooks'
import { FormSchema, useAppForm } from '@repo/ui/hooks'

import {
  Button,
  Col,
  Form,
  FormField,
  FormItemWrapper,
  Icon,
  InputPassword,
  Row,
  Typography,
} from '@repo/ui/components'
import { useChangePasswordSchema } from '../../schemas'

interface ChangePasswordFormProps {
  onSuccess: (data: { password: string }) => void
  isLoading: boolean
  isSetPassword: boolean
}

export const ChangePasswordForm = ({
  onSuccess,
  isLoading,
  isSetPassword,
}: ChangePasswordFormProps) => {
  const { t } = useTranslate()
  const { changePasswordSchema: schema, validationRules } =
    useChangePasswordSchema()
  const { form } = useAppForm({
    formSchema: schema,
  })

  const onSubmit = async (data: FormSchema<typeof schema>) => {
    onSuccess(data)
  }

  const password = form.watch('password')

  const validationMessages = [
    {
      key: 'common_validation_minLength',
      value: '',
      isValid: password?.length >= validationRules.minLength,
    },
    {
      key: 'common_validation_lowercase',
      value: 'abc ...',
      isValid: validationRules.lowercase.test(password ?? ''),
    },
    {
      key: 'common_validation_uppercase',
      value: 'ABC ...',
      isValid: validationRules.uppercase.test(password ?? ''),
    },
    {
      key: 'common_validation_number',
      value: '123 ...',
      isValid: validationRules.number.test(password ?? ''),
    },
  ]

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className='space-y-3xl w-full'
      >
        <FormField
          control={form.control}
          name='password'
          displayValidation={false}
          render={({ field }) => (
            <>
              <FormItemWrapper
                title={t('changePassword_newPassword')}
                inputSize='lg'
              >
                <InputPassword
                  autoComplete='new-password'
                  autoFocus
                  {...field}
                />
              </FormItemWrapper>
              <Col className='gap-xs -mt-md'>
                {validationMessages?.map((item) => (
                  <Row key={item.key} className='gap-xs'>
                    <Icon
                      name='InfoCircleGhost'
                      size='sm'
                      color={
                        item.isValid
                          ? 'fill-positive-surface-100'
                          : 'fill-negative-surface-100'
                      }
                    />
                    <Row className='justify-between'>
                      <Typography
                        size='sm'
                        weight='regular'
                        color={
                          item.isValid
                            ? 'text-positive-surface-100'
                            : 'text-negative-surface-100'
                        }
                      >
                        {t(item.key as keyof typeof t)}
                      </Typography>
                      <Typography
                        size='sm'
                        weight='regular'
                        color='text-neutral-surface-60'
                        className='ltr'
                      >
                        {item.value}
                      </Typography>
                    </Row>
                  </Row>
                ))}
              </Col>
            </>
          )}
        />
        <FormField
          control={form.control}
          name='confirmPassword'
          render={({ field }) => (
            <FormItemWrapper
              title={t('changePassword_confirmPassword')}
              inputSize='lg'
            >
              <InputPassword autoComplete='new-password' {...field} />
            </FormItemWrapper>
          )}
        />
        <Button
          className='w-full'
          type='submit'
          variant='fill'
          size='lg'
          loading={isLoading}
        >
          {isSetPassword
            ? t('changePassword_submit_set_password')
            : t('changePassword_submit')}
        </Button>
      </form>
    </Form>
  )
}
